// Pure, framework-free logic for the Icon Wizard composition. The Vuex module
// (`iconBuilder.js`) holds reactive state and orchestration and delegates every
// non-trivial transform here so it can be unit-tested without a store.

// Maximum layers a single Icon Wizard composition can have.
export const MAX_LAYERS = 20

// The two duotone tones. `layers` is kept sorted with every secondary before
// every primary: the tray renders reversed, so that puts primary on top and
// secondary below, matching the divider the spec describes.
//
// The tone lives on the layer rather than being derived from a stored divider
// position. Reordering is live — the array mutates on every dragover tick — so
// a fixed boundary would re-tone layers nobody dragged as others slid past it.
export const PRIMARY = 'primary'
export const SECONDARY = 'secondary'

// What a secondary tone renders at wherever a two-tone creation is drawn. The
// three surfaces that draw one — the gallery frame, the gallery detail modal
// and the moderation queue — each sit in a different kind of container (a
// component, a `<g>` inside a composed SVG, a bare swatch), so they cannot
// share markup. They can share this.
export const SECONDARY_TONE_OPACITY = 0.4

// The square canvas every composition is combined into.
export const CANVAS_SIZE = 640

// Kits store icons the way Font Awesome ships them: 512 units tall, with
// anything outside that band as overflow. The canvas is that band plus 64
// units of padding above and below.
export const KIT_ICON_HEIGHT = 512

// Viggo turns a cutout thickness (in pt) into a uniform outward offset of the
// layer's geometry. Measured against viggo for both cutout variants at several
// thicknesses: 1pt is 32 canvas units — the canvas scale itself, 640 units
// across a 20pt icon.
export const CUTOUT_UNITS_PER_PT = CANVAS_SIZE / 20

// Cutout stroke thickness a fresh layer starts at, and what the stroke control
// resets to.
export const DEFAULT_CUTOUT_VALUE = 1.5

// Viggo combine transform keys — currently 1:1 with internal names. This map
// exists so we can rename internal types without changing the Viggo API call.
const TRANSFORM_KEY_MAP = {}

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

// A fresh layer for the given seed icon, with the composition defaults.
export function createLayer({ iconName, prefix, path, width, height, positionType, method, group, cutoutPath }) {
  return {
    id: generateId(),
    // Which duotone tone this layer belongs to. Primary unless said otherwise:
    // a composition that never turns duotone on is entirely primary, which is
    // also what makes it render as a single shape.
    group: group || PRIMARY,
    iconName,
    prefix,
    path,
    width,
    height,
    method: method || 'Add',
    cutoutValue: DEFAULT_CUTOUT_VALUE,
    cutoutPath: cutoutPath || null,
    matchLibraryCutout: !!cutoutPath,
    positionType: positionType || 'Center',
    positionX: 0,
    positionY: 0,
    operations: []
  }
}

// A fresh transform operation for a layer.
export function createOperation({ type, value }) {
  return { id: generateId(), type, value }
}

// A deep copy of a layer with fresh ids on the layer and every operation, so
// the copy can be mutated independently of its source.
export function copyLayerData(source) {
  return {
    id: generateId(),
    group: layerGroup(source),
    iconName: source.iconName,
    prefix: source.prefix,
    path: source.path,
    width: source.width,
    height: source.height,
    method: source.method,
    cutoutValue: source.cutoutValue,
    cutoutPath: source.cutoutPath,
    matchLibraryCutout: source.matchLibraryCutout,
    positionType: source.positionType,
    positionX: source.positionX,
    positionY: source.positionY,
    operations: source.operations.map((op) => ({ id: generateId(), type: op.type, value: op.value }))
  }
}

// The layers of one tone, in order.
// A layer's tone, defaulting to primary. Every read goes through this: a layer
// saved before duotone existed has no `group` at all, and each site that
// spelled the fallback out itself was a place the default could drift.
export function layerGroup(layer) {
  return layer.group || PRIMARY
}

export function layersInGroup(layers, group) {
  return layers.filter((l) => layerGroup(l) === group)
}

// `layers` re-sorted so every secondary precedes every primary, preserving the
// relative order within each tone. Sorting is stable in every engine we target,
// so this is enough to restore the invariant after a tone change.
export function sortLayersByGroup(layers) {
  return [...layersInGroup(layers, SECONDARY), ...layersInGroup(layers, PRIMARY)]
}

// Whether the sorted invariant holds: no secondary layer may appear after a
// primary one.
export function isGroupSorted(layers) {
  const firstPrimary = layers.findIndex((l) => layerGroup(l) === PRIMARY)
  if (firstPrimary === -1) return true
  return layers.slice(firstPrimary).every((l) => layerGroup(l) === PRIMARY)
}

// The ids of every layer between `fromId` and `toId` inclusive, in list order
// so a range reads the same whichever end it was drawn from. Empty when either
// end is missing — the caller falls back to a plain selection.
export function layerIdRange(layers, fromId, toId) {
  const from = layers.findIndex((l) => l.id === fromId)
  const to = layers.findIndex((l) => l.id === toId)
  if (from === -1 || to === -1) return []
  const start = Math.min(from, to)
  const end = Math.max(from, to)
  return layers.slice(start, end + 1).map((l) => l.id)
}

// `ids` narrowed to the layers that actually exist, in list order. Selection is
// stored this way so a reorder can't scramble it and a removed layer can't
// linger in it.
export function orderedLayerIds(layers, ids) {
  return layers.filter((l) => ids.includes(l.id)).map((l) => l.id)
}

// A new layer list with a copy of each of `layerIds` inserted right after its
// source, and the ids of those copies.
export function copyLayersInList(layers, layerIds) {
  const copyIds = []
  const next = []
  layers.forEach((layer) => {
    next.push(layer)
    if (layerIds.includes(layer.id)) {
      const copy = copyLayerData(layer)
      copyIds.push(copy.id)
      next.push(copy)
    }
  })
  return { layers: next, copyIds }
}

// A new layer list with `layerId` moved by `direction` (±1). Returns the same
// list unchanged when the id isn't found or the move would fall off either end.
export function moveLayerInList(layers, layerId, direction) {
  const idx = layers.findIndex((l) => l.id === layerId)
  if (idx === -1) return layers
  const newIdx = idx + direction
  if (newIdx < 0 || newIdx >= layers.length) return layers
  const next = [...layers]
  const [layer] = next.splice(idx, 1)
  next.splice(newIdx, 0, layer)
  return next
}

export function isCutoutMethod(method) {
  return method === 'AddWithOuterCutout' || method === 'AddWithContourCutout'
}

// Whether the layer's cutout is the shape it shipped with rather than a stroke
// around itself.
export function usesLibraryCutout(layer) {
  return !!layer.cutoutPath && !!layer.matchLibraryCutout && isCutoutMethod(layer.method)
}

// Translate the internal layer shape into the operation objects the Viggo
// combine API expects.
export function buildCombineOperations(layers) {
  return layers.flatMap(layerOperations)
}

function layerOperations(layer) {
  const op = baseOperation(layer, layer.path)

  // The shipped cutout goes in as its own Subtract, placed and transformed as
  // the layer is, so it follows every flip, nudge and scale.
  if (usesLibraryCutout(layer)) {
    return [buildLibraryCutoutOperation(layer, 'Subtract'), op]
  }

  if (layer.method === 'Subtract') {
    op.method = 'Subtract'
  } else if (isCutoutMethod(layer.method)) {
    // thickness is in pt; viggo converts to internal d offset.
    const thickness = Math.max(0, layer.cutoutValue ?? 0)
    if (thickness > 0) {
      const variantKey = layer.method === 'AddWithContourCutout' ? 'addWithContourCutout' : 'addWithOuterCutout'
      op.method = { [variantKey]: { thickness, sharp: false } }
    }
  }
  // "Add" is the default, no need to set it

  return [op]
}

function baseOperation(layer, primaryPath) {
  const position = layer.positionType === 'Coordinates'
    ? { x: layer.positionX, y: layer.positionY }
    : layer.positionType

  // TODO (Viggo): Position presets (TopLeft, BottomRight, etc.) are calculated
  // against the layer's viewBox width/height, but some icons have viewBox dimensions
  // that don't match the actual path bounding box. This causes icons to not visually
  // align to corners as expected. Viggo should support an option to position based on
  // the path's actual bounding box rather than the declared viewBox dimensions.
  const op = {
    width: layer.width,
    height: layer.height,
    primaryPath,
    position
  }

  if (layer.operations.length > 0) {
    op.transforms = layer.operations.map((t) => {
      const key = TRANSFORM_KEY_MAP[t.type] || t.type
      return { [key]: t.value }
    })
  }

  return op
}

export function buildLibraryCutoutOperation(layer, method) {
  const op = baseOperation(layer, layer.cutoutPath)
  return method ? { ...op, method } : op
}

// The operand for tracing one layer on its own: positioned and transformed
// exactly as it sits in the composition, but always combined as a plain `Add`.
// A Subtract or cutout layer contributes a hole rather than a shape, and a hole
// has no outline to draw — its own geometry is what we want to trace.
export function buildOutlineOperation(layer) {
  const [operation] = buildCombineOperations([{ ...layer, method: 'Add' }])
  return operation
}

// How far a layer's cutout stroke reaches beyond its shape, in canvas units,
// or 0 when the layer has no stroke. Because the cutout is a uniform offset,
// this doubles as the SVG stroke radius that reproduces it, which is how the
// canvas draws the stroke's edge without asking viggo to cut it.
export function strokeOffsetForLayer(layer) {
  // A shipped cutout is a fixed shape, not a stroke around the layer.
  if (usesLibraryCutout(layer)) return 0
  const [operation] = buildCombineOperations([layer])
  // `buildCombineOperations` encodes a cutout as an object and everything else
  // as a string or nothing, so this is also the "has a stroke" test — it already
  // drops the method when the thickness is zero or negative.
  const method = operation.method
  if (!method || typeof method !== 'object') return 0

  const cutout = method.addWithOuterCutout || method.addWithContourCutout
  return cutout ? cutout.thickness * CUTOUT_UNITS_PER_PT : 0
}

// Viggo validates every path coordinate against the i16 range a font glyph can
// hold and rejects the operand outright past it, so this is the ceiling for how
// far any geometry — ours or a layer's — can reach.
const COORDINATE_LIMIT = 32767

// How far past the canvas the crop frame reaches. Only geometry inside the
// frame is removed and a layer can be translated a long way, so the frame wants
// to be as large as it can be: anything beyond its outer edge escapes the crop.
// That makes the coordinate limit the natural size, less a little headroom.
const CROP_MARGIN = COORDINATE_LIMIT - CANVAS_SIZE * 2

// Everything outside the canvas, as a frame: an outer rectangle far clear of
// the canvas wound one way, the canvas itself wound the other, so the ring
// between them is the region to remove.
const CROP_FRAME_PATH = [
  `M${-CROP_MARGIN} ${-CROP_MARGIN}`,
  `L${CANVAS_SIZE + CROP_MARGIN} ${-CROP_MARGIN}`,
  `L${CANVAS_SIZE + CROP_MARGIN} ${CANVAS_SIZE + CROP_MARGIN}`,
  `L${-CROP_MARGIN} ${CANVAS_SIZE + CROP_MARGIN} Z`,
  'M0 0',
  `L0 ${CANVAS_SIZE}`,
  `L${CANVAS_SIZE} ${CANVAS_SIZE}`,
  `L${CANVAS_SIZE} 0 Z`
].join(' ')

// A synthetic operand that removes `path` from whatever it is appended to.
// Built exactly like the crop frame — not from a layer — and used to cut the
// primary tone out of the secondary so the two never overlap. The path is
// already a combine result, so it is in canvas space and centres like the frame.
export function buildSubtractOperation(path) {
  return {
    width: CANVAS_SIZE,
    height: CANVAS_SIZE,
    primaryPath: path,
    position: 'Center',
    method: 'Subtract'
  }
}

// The operand that crops a composition to the canvas. Subtracting the frame
// clamps anything a layer pushes past the edge, so the crop is part of the
// icon rather than an accident of whoever rasterizes it last.
export function buildCropOperation() {
  return {
    width: CANVAS_SIZE,
    height: CANVAS_SIZE,
    primaryPath: CROP_FRAME_PATH,
    position: 'Center',
    method: 'Subtract'
  }
}

// viggo combine errors include `combine: operand <i>: …` — pull the index so
// we can attribute the failure to a specific operand.
function parseOperandIndex(message) {
  const match = /operand\s+(\d+)/i.exec(message)
  return match ? parseInt(match[1], 10) : -1
}

// Run viggo combine on `operations` and return { width, height, path }.
// `viggo` is injected (the loaded WASM module) so this stays pure and testable.
// Applies the contour→outer fallback if the first combine throws because a
// contour cutout produced no geometry for a specific operand.
//
// Every caller comes through here — the preview, both downloads, the share, and
// the selection trace — so the crop lands last and lands once, and they all
// agree on where the icon ends. It is appended rather than inserted so the
// operand indices viggo reports in errors still line up with the layers.
export function combineWithFallback(viggo, operations) {
  operations = [...operations, buildCropOperation()]

  const buildCanvas = (ops) => {
    const canvas = viggo.parseStandard(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CANVAS_SIZE} ${CANVAS_SIZE}"></svg>`)
    canvas.combine(ops)
    return canvas
  }

  let canvas
  try {
    canvas = buildCanvas(operations)
  } catch (e) {
    // Contour cutout can fail when an operand's path geometry is too small or
    // otherwise can't be stroked. Fall back to outer cutout for that operand
    // and retry — outer cutout offsets the region rather than stroking the
    // contour, so it survives degenerate geometry.
    const message = String((e && e.message) || e)
    const operandIndex = parseOperandIndex(message)
    if (operandIndex >= 0 && /contour cutout/i.test(message) && operations[operandIndex]?.method?.addWithContourCutout) {
      const inner = operations[operandIndex].method.addWithContourCutout
      operations = operations.map((op, i) =>
        i === operandIndex ? { ...op, method: { addWithOuterCutout: inner } } : op
      )
      canvas = buildCanvas(operations)
    } else {
      throw e
    }
  }

  return {
    width: canvas.width(),
    height: canvas.height(),
    path: canvas.primaryPath()
  }
}

// A composition in the shape a Kit stores: 512 tall, trimmed to a standard
// width, exactly as an upload of the same icon would be.
export function formatForKit(viggo, { path, pathSecondary }) {
  const width = uploadWidth(viggo, path, pathSecondary)
  const shift = (canvasPath) => {
    if (!canvasPath) return canvasPath
    const kitCanvas = viggo.parseStandard(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${KIT_ICON_HEIGHT}"></svg>`)
    kitCanvas.combine([{ width: CANVAS_SIZE, height: CANVAS_SIZE, primaryPath: canvasPath, position: 'Center' }])
    return kitCanvas.primaryPath()
  }

  return {
    width,
    height: KIT_ICON_HEIGHT,
    path: shift(path),
    pathSecondary: shift(pathSecondary)
  }
}

// Import is what picks an uploaded icon's width, so it picks this one too. Only
// the width is taken: import re-optimizes the paths, and the Wizard's tones are
// already final.
function uploadWidth(viggo, path, pathSecondary) {
  const svg = viggo.SvgIcon.fromParts(CANVAS_SIZE, CANVAS_SIZE, path || null, pathSecondary || null, !!pathSecondary).toString()
  const status = viggo.SvgIcon.import(svg, undefined)
  const imported = status.takeSvg()
  return !status.hasError() && imported ? imported.width() : CANVAS_SIZE
}

// The share/composition display name: the layers' icon names joined, capped to
// 80 chars with an ellipsis.
export function shareNameFromLayers(layers) {
  const rawName = layers.map((l) => l.iconName).join(' + ')
  return rawName.length > 80 ? `${rawName.slice(0, 77)}...` : rawName
}
